import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/perms";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Building2, Palette, CreditCard, Film } from "lucide-react";
import { TextAlertsCard } from "@/components/TextAlertsCard";
import { PushSetup } from "@/components/PushSetup";
import { CommercialCard } from "@/components/CommercialCard";
import TermsDialog from "@/components/TermsDialog";

const Field = ({ label, testId, value, onChange, placeholder, type = "text", disabled }) => (
  <div>
    <Label className="text-xs uppercase tracking-wider text-muted-foreground">{label}</Label>
    <Input
      data-testid={testId}
      type={type}
      value={value || ""}
      disabled={disabled}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className="mt-1.5 h-11"
    />
  </div>
);

const Card = ({ icon: Icon, title, description, children, testId }) => (
  <section data-testid={testId} className="rounded-lg border border-border bg-white overflow-hidden">
    <div className="flex items-start gap-3 px-5 py-4 border-b border-border">
      <Icon className="h-4 w-4 text-gold mt-0.5" />
      <div>
        <h2 className="font-display text-base md:text-lg font-semibold text-royal-deep">{title}</h2>
        <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
      </div>
    </div>
    <div className="p-5">{children}</div>
  </section>
);

const PlanPicker = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [annual, setAnnual] = useState(false);
  const [legal, setLegal] = useState(null);
  const [pending, setPending] = useState(null); // { kind: "trial" } | { kind: "plan", plan }

  useEffect(() => {
    api.get("/plans").then(({ data }) => setData(data));
    api.get("/legal/status").then(({ data }) => setLegal(data)).catch(() => {});
  }, []);

  const gate = (action) => {
    if (legal && !legal.accepted) { setPending(action); return; }
    run(action);
  };

  const run = async (action) => {
    setPending(null);
    if (action.kind === "trial") return startTrial();
    return upgrade(action.plan);
  };

  const afterAccept = async () => {
    const current = pending;
    try {
      const { data: s } = await api.get("/legal/status");
      setLegal(s);
    } catch { /* status refresh is cosmetic */ }
    if (current) await run(current);
  };

  const upgrade = async (plan) => {
    setBusy(plan);
    try {
      const { data: res } = await api.post("/plans/checkout",
        { plan, interval: annual ? "annual" : "month", origin_url: window.location.origin });
      window.location.href = res.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not start checkout");
      setBusy("");
    }
  };

  const enterpriseInquiry = async () => {
    setBusy("enterprise");
    try {
      const { data: res } = await api.post("/plans/enterprise-inquiry", {});
      toast.success(res.message);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send that request");
    }
    setBusy("");
  };

  if (!data) return null;

  const trialing = data.status === "trialing" || data.current === "trial";
  const trialEnds = data.trial_ends_at ? new Date(data.trial_ends_at).toLocaleDateString() : null;

  const startTrial = async () => {
    setBusy("trial");
    try {
      const { data: res } = await api.post("/plans/start-trial",
        { origin_url: window.location.origin });
      window.location.href = res.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not start the trial");
      setBusy("");
    }
  };

  const openPortal = async () => {
    setBusy("portal");
    try {
      const { data: res } = await api.post("/plans/billing-portal",
        { origin_url: window.location.origin });
      window.location.href = res.portal_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not open billing");
      setBusy("");
    }
  };

  const quoteFor = (value) => data.quotes?.[value]?.[annual ? "annual" : "month"];
  const better = data.better_value;

  return (
    <Card icon={CreditCard} title="Choose your plan"
      description={`You are on ${data.current.toUpperCase()} · using ${data.usage.properties} propert${
        data.usage.properties === 1 ? "y" : "ies"} and ${data.usage.users} user seat(s).`}
      testId="plan-picker-card">
      <div className="mb-4 rounded-md border border-gold/50 bg-gold/5 px-3.5 py-3"
           data-testid="pricing-philosophy">
        <p className="text-xs font-semibold text-royal-deep">
          You pay for properties and users — never for features.
        </p>
        <p className="text-[11px] text-muted-foreground mt-1">
          Starter, Professional and Business are the same complete platform. Nothing is locked
          behind a higher plan and there are no per-user fees. Going over your included properties
          never forces a plan change — extra properties simply bill at your plan's rate.
        </p>
      </div>
      {better && (
        <div className="mb-4 rounded-md border border-royal/40 bg-royal/5 px-3.5 py-3"
             data-testid="better-value-tip">
          <p className="text-xs font-semibold text-royal-deep">
            You may save money by switching to {better.label}.
          </p>
          <p className="text-[11px] text-muted-foreground mt-1">
            At {data.usage.properties} properties you would pay ${better.total.toLocaleString()}
            {better.interval === "annual" ? "/yr" : "/mo"} instead of $
            {better.current_total.toLocaleString()}
            {better.interval === "annual" ? "/yr" : "/mo"} — a saving of $
            {better.saves.toLocaleString()}. Staying put is fine too.
          </p>
        </div>
      )}
      {trialing && (
        <div data-testid="trial-billing-banner"
             className="mb-4 rounded-md border border-gold/60 bg-gold/10 px-3.5 py-3">
          <p className="text-xs font-semibold text-royal-deep">
            {data.card_on_file
              ? `Trial active${trialEnds ? ` until ${trialEnds}` : ""} — $0 today. Your first payment is taken automatically the day it ends, then monthly on that date.`
              : `Your trial needs a card on file${trialEnds ? ` before ${trialEnds}` : ""}. Nothing is charged today.`}
          </p>
          <div className="mt-2.5 flex flex-wrap gap-2">
            {!data.card_on_file && (
              <Button data-testid="start-trial-btn" disabled={busy === "trial"}
                onClick={() => gate({ kind: "trial" })}
                className="h-9 text-xs bg-royal hover:bg-royal-deep text-white">
                {busy === "trial" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Add card & start trial"}
              </Button>
            )}
            {data.card_on_file && (
              <Button data-testid="billing-portal-btn" variant="outline" disabled={busy === "portal"}
                onClick={openPortal}
                className="h-9 text-xs border-gold text-royal-deep hover:bg-gold/10">
                {busy === "portal" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Manage card & billing"}
              </Button>
            )}
          </div>
        </div>
      )}
      <div className="mb-4 inline-flex rounded-full border border-border bg-secondary/60 p-1"
           data-testid="settings-billing-toggle">
        {[["Monthly", false], ["Annual · save 16%", true]].map(([label, val]) => (
          <button key={label} type="button" onClick={() => setAnnual(val)}
            data-testid={val ? "settings-billing-annual" : "settings-billing-monthly"}
            aria-pressed={annual === val}
            className={`rounded-full px-3.5 min-h-[38px] text-xs font-semibold transition-colors duration-200
                        ${annual === val ? "bg-royal text-white" : "text-royal-deep hover:text-royal"}`}>
            {label}
          </button>
        ))}
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {data.plans.filter((p) => p.value !== "trial").map((p) => {
          const q = quoteFor(p.value);
          return (
          <div key={p.value} data-testid={`plan-${p.value}`}
            className={`relative rounded-lg border p-4 flex flex-col ${
              data.current === p.value ? "border-gold bg-gold/5"
                : p.popular ? "border-gold/70" : "border-border"}`}>
            {p.popular && (
              <span data-testid="settings-most-popular"
                    className="absolute -top-2.5 left-4 rounded-full bg-gold px-2.5 py-0.5 text-[10px]
                               font-bold uppercase tracking-[0.16em] text-royal-deep">
                Most Popular
              </span>
            )}
            <p data-testid={`settings-scale-${p.value}`}
               className="text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
              {p.scale}
            </p>
            <p className="font-display font-semibold text-royal-deep mt-0.5">{p.label}</p>
            <p className="font-display text-2xl font-bold text-royal mt-1">
              {p.contact_only ? "Custom pricing" : `$${(annual ? p.annual_price : p.price).toLocaleString()}`}
              {!p.contact_only && (
                <span className="text-xs font-normal text-muted-foreground">{annual ? "/yr" : "/mo"}</span>
              )}
            </p>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              {p.contact_only ? `Starting at $${p.starting_price}+/month`
                : annual ? `$${Math.round(p.annual_price / 12)}/mo equivalent · save $${(p.price * 12 - p.annual_price).toLocaleString()} vs $${(p.price * 12).toLocaleString()} monthly`
                : `or $${p.annual_price.toLocaleString()}/year`}
            </p>
            <p className="text-xs text-muted-foreground mt-1.5">{p.blurb}</p>
            <p className="text-[11px] text-royal-deep mt-2 font-medium"
               data-testid={`settings-includes-${p.value}`}>
              {p.max_properties ?? "Unlimited"} properties · {p.max_users ?? "Unlimited"} users
              {p.overage_rate ? ` · extra properties $${p.overage_rate}/property/mo` : ""}
            </p>
            {!p.contact_only && (
              <p className="text-[11px] text-muted-foreground mt-1">Complete platform included.</p>
            )}
            {q?.extra_properties > 0 && (
              <p className="text-[11px] text-royal-deep mt-2 font-medium"
                 data-testid={`overage-${p.value}`}>
                Your {data.usage.properties} properties: ${q.base.toLocaleString()} +
                {" "}{q.extra_properties} extra × ${p.overage_rate}
                {annual ? "/mo × 12" : ""} = <strong>${q.total.toLocaleString()}</strong>
                {annual ? "/yr" : "/mo"}
              </p>
            )}
            {data.best_value_plan === p.value && data.current !== p.value && (
              <p className="text-[11px] text-royal mt-2 font-semibold"
                 data-testid={`best-value-${p.value}`}>
                Best value at your size
              </p>
            )}
            {data.current === p.value ? (
              <p className="mt-3 text-[11px] font-semibold text-gold uppercase tracking-wider">Current plan</p>
            ) : p.contact_only ? (
              <Button data-testid="enterprise-inquiry-btn" disabled={busy === "enterprise"}
                onClick={enterpriseInquiry} variant="outline"
                className="mt-3 h-10 text-xs border-gold text-royal-deep hover:bg-gold/10">
                {busy === "enterprise" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Talk to our team"}
              </Button>
            ) : (
              <Button data-testid={`upgrade-${p.value}-btn`} disabled={busy === p.value}
                onClick={() => gate({ kind: "plan", plan: p.value })}
                className="mt-3 h-10 text-xs bg-royal hover:bg-royal-deep text-white">
                {busy === p.value ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : `Switch to ${p.label}`}
              </Button>
            )}
          </div>
          );
        })}
      </div>
      <p className="mt-4 text-[11px] text-muted-foreground">
        Your price is the plan base plus $ per extra property — no per-user fees, no usage fees and
        no feature add-ons. Subscriptions are separate from card payment processing — the 2% credit
        card surcharge on customer invoices is unaffected by your plan.
      </p>
      <div className="mt-3 rounded-md border border-border bg-secondary/40 px-3.5 py-3"
           data-testid="legal-acceptance-row">
        <p className="text-[11px] text-muted-foreground">
          {legal?.accepted
            ? `Terms of Service v${legal.accepted_version} accepted${
                legal.accepted_at ? ` on ${new Date(legal.accepted_at).toLocaleString()}` : ""}${
                legal.accepted_by ? ` by ${legal.accepted_by}` : ""}.`
            : "Our Terms of Service have not been accepted for this company yet — you'll be asked to review them before checkout."}
          {" "}
          <a href="/terms" target="_blank" rel="noreferrer" data-testid="settings-terms-link"
             className="font-semibold text-royal underline">Terms</a>
          {" · "}
          <a href="/privacy" target="_blank" rel="noreferrer" data-testid="settings-privacy-link"
             className="font-semibold text-royal underline">Privacy Policy</a>
        </p>
      </div>

      <TermsDialog open={!!pending} onClose={() => setPending(null)} onAccepted={afterAccept}
        context="settings" />
    </Card>
  );
};

export default function Settings() {
  const { user, refresh } = useAuth();
  const editable = can(user, "org:update");
  const [org, setOrg] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/orgs/current").then(({ data }) => setOrg(data));
  }, []);

  const set = (k, v) => setOrg((o) => ({ ...o, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.patch("/orgs/current", {
        name: org.name,
        phone: org.phone,
        website: org.website,
        tagline: org.tagline,
        timezone: org.timezone,
        email_from_name: org.email_from_name,
        primary_color: org.primary_color,
        accent_color: org.accent_color,
      });
      setOrg(data);
      await refresh();
      toast.success("Organization settings saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save settings");
    }
    setBusy(false);
  };

  if (!org) return <Loader2 className="h-6 w-6 animate-spin text-royal" />;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="settings-page">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">Organization</p>
        <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-royal-deep mt-2">Company Settings</h1>
        <p className="text-muted-foreground mt-3 text-sm md:text-base">
          How your company appears across Cornerstone Ops, its reports and its emails.
        </p>
      </div>

      <form onSubmit={save} className="space-y-6">
        <Card
          icon={Building2}
          title="Company profile"
          description="Shown in the app header, PDF reports and owner notifications."
          testId="org-profile-card"
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Company name" testId="org-name-input" value={org.name} disabled={!editable}
              onChange={(v) => set("name", v)} />
            <Field label="Phone" testId="org-phone-input" value={org.phone} disabled={!editable}
              onChange={(v) => set("phone", v)} />
            <Field label="Website" testId="org-website-input" value={org.website} disabled={!editable}
              onChange={(v) => set("website", v)} placeholder="https://" />
            <Field label="Email sender name" testId="org-email-from-input" value={org.email_from_name}
              disabled={!editable} onChange={(v) => set("email_from_name", v)} />
            <div className="sm:col-span-2">
              <Field label="Tagline" testId="org-tagline-input" value={org.tagline} disabled={!editable}
                onChange={(v) => set("tagline", v)} placeholder="A short line about your company" />
            </div>
          </div>
        </Card>

        <Card
          icon={Palette}
          title="Branding"
          description="Your brand colours. Used by reports and, in future, your own branded portal."
          testId="org-branding-card"
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Primary colour</Label>
              <div className="mt-1.5 flex items-center gap-3">
                <input
                  data-testid="org-primary-color-input"
                  type="color"
                  disabled={!editable}
                  value={org.primary_color || "#1b2a63"}
                  onChange={(e) => set("primary_color", e.target.value)}
                  className="h-11 w-16 rounded-md border border-border bg-white p-1"
                />
                <span className="text-sm text-muted-foreground">{org.primary_color}</span>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Accent colour</Label>
              <div className="mt-1.5 flex items-center gap-3">
                <input
                  data-testid="org-accent-color-input"
                  type="color"
                  disabled={!editable}
                  value={org.accent_color || "#d4af37"}
                  onChange={(e) => set("accent_color", e.target.value)}
                  className="h-11 w-16 rounded-md border border-border bg-white p-1"
                />
                <span className="text-sm text-muted-foreground">{org.accent_color}</span>
              </div>
            </div>
          </div>
        </Card>

        <Card
          icon={CreditCard}
          title="Plan & usage"
          description="Your current tier and how much of it you're using."
          testId="org-plan-card"
        >
          <div className="grid gap-4 sm:grid-cols-3">
            {[
              ["Plan", (org.plan || "trial").toUpperCase()],
              ["Status", (!org.plan_status || org.plan_status === "pre_trial")
                ? ((org.plan || "trial") === "trial" ? "Trial not started" : "Active")
                : org.plan_status.replace("_", " ")],
              ["Property limit", org.max_properties ?? "Unlimited"],
              ["User limit", org.max_users ?? "Unlimited"],
              ["Trial ends", org.trial_ends_at ? new Date(org.trial_ends_at).toLocaleDateString() : "—"],
              ["Your role", org.role_label || "—"],
            ].map(([k, v]) => (
              <div key={k} className="rounded-md border border-border p-3">
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{k}</p>
                <p className="font-display text-base font-semibold text-royal-deep mt-1 capitalize">{v}</p>
              </div>
            ))}
          </div>
        </Card>

        {can(user, "billing:manage") && <PlanPicker />}
        {can(user, "org:update") && <CommercialCard />}
        {can(user, "org:update") && (
          <CommercialCard film="ad2" route="/ad2"
            title="The Weather Doesn't Wait — 45 seconds"
            blurb="The weather commercial: storm conditions turning into an automated inspection and a crew alert. No real property or owner names appear in it." />
        )}
        {can(user, "org:update") && (
          <CommercialCard film="ad3" route="/ad3"
            title="Stop Managing The Pieces — 30 seconds"
            blurb="The disruptor spot: the scattered old way, then the whole operation running as one command center. Every interface is drawn in-house, with no real property or owner names." />
        )}

        <PushSetup />
        <TextAlertsCard />

        {editable && (
          <Button
            data-testid="save-org-btn"
            type="submit"
            disabled={busy}
            className="w-full sm:w-auto h-12 px-8 bg-royal hover:bg-royal-deep text-white font-semibold"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save settings"}
          </Button>
        )}
      </form>
    </div>
  );
}
